import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FormikErrors, useFormik } from 'formik'

import { useToastContext } from '../../../../../../contexts'
import { WebEdiDto } from '../../../../../../data/models'
import { getHandleableError } from '../../../../../../utils/errors/isXiaError'
import {
  FieldToValidate,
  validateFormikFields,
} from '../../../../../../utils/helper/validateFormikFields'
import {
  containsDisallowedCharacters,
  getDisallowedCharacters,
} from '../../../../../../utils/regex'
import { useConfigContext } from '../../../../../ui-config'
import { requestAttachmentUpload, useInvoiceById } from '../../../../data'
import { UploadAttachmentDialog } from './UploadAttachmentDialog'

interface FormValues {
  attachment: File
  category: string
}

type FormFieldName = 'attachment' | 'category'

type Props = {
  invoiceId: WebEdiDto['Id']
  processId: string
}

export const UploadAttachmentDialogContainer: React.FC<Props> = ({
  invoiceId,
  processId,
}) => {
  const { t } = useTranslation()
  const [visible, setVisible] = useState(false)

  const { uiConfig: config } = useConfigContext()

  const { showToast } = useToastContext()
  const [isRequestRunning, setIsRequestRunning] = useState<boolean>(false)

  const { data, mutate } = useInvoiceById(invoiceId)

  const [isChecking, setIsChecking] = useState(false)
  const [showResult, setShowResult] = useState(false)
  const [isUploadFileVisible, setIsUploadFileVisible] = useState(true)

  // ************************************************************
  // Formik
  // ************************************************************
  function hasSameName(filename: File) {
    const filesWithSameName =
      data?.Attachments.filter(
        attachment => attachment.Name === filename.name,
      ) || []

    return filesWithSameName.length > 0
  }

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      attachment: {} as File,
      category: '',
    },
    validate: formData => {
      let errors: FormikErrors<FormValues> = {}

      const fieldsToValidate: FieldToValidate[] = [
        {
          fieldName: 'attachment',
          config: config.invoice.uploadAttachment.attachment,
        },
        {
          fieldName: 'category',
          config: config.invoice.uploadAttachment.category,
        },
      ]

      validateFormikFields(fieldsToValidate, formData, errors)

      // Attachment has same name
      if (formData.attachment && hasSameName(formData.attachment!)) {
        errors.attachment = t(
          'invoice.attachments.table.upload.dialog.form.attachment.validation.fileHasSameName',
        )
      }

      // Check filename for special characters
      const fileName = formData.attachment?.name
      const fileNameWithoutExtension = fileName?.slice(
        0,
        fileName.lastIndexOf('.'),
      )
      if (containsDisallowedCharacters(fileNameWithoutExtension)) {
        errors.attachment = t(
          'invoice.attachments.table.upload.dialog.form.attachment.validation.attachmentContainsDisallowedCharacters',
          {
            disallowedCharacters: getDisallowedCharacters(
              fileNameWithoutExtension,
            ),
          },
        )
      }

      // Category AI is already taken
      const hasAiInvoiceDocument = data?.Attachments.some(
        attachment => attachment.Category === 'aiInvoiceDocument',
      )
      if (formData.category === 'aiInvoiceDocument' && hasAiInvoiceDocument) {
        errors.category = t(
          'invoice.attachments.table.upload.dialog.form.category.validation.aiAlreadyExist',
        )
      }

      if (
        formData.attachment &&
        formData.attachment.type !== 'application/pdf' &&
        formData.category &&
        formData.category === 'aiInvoiceDocument'
      ) {
        errors.attachment = t(
          'invoice.attachments.table.upload.dialog.form.attachment.validation.wrongFiletype',
        )
      }

      return errors
    },
    onSubmit: async formData => {
      await submitForm(formData)
    },
  })

  const isFormFieldValid = (name: FormFieldName) =>
    !(formik.touched[name] && formik.errors[name])

  const getFormErrorMessage = (name: FormFieldName) => {
    const error = formik.errors[name]

    return (
      !isFormFieldValid(name) &&
      typeof error === 'string' && <small className="p-error">{error}</small>
    )
  }

  const submitForm = async (file: FormValues) => {
    setIsRequestRunning(true)

    try {
      // Check if AI check is active
      if (file.category === 'aiInvoiceDocument') {
        setIsUploadFileVisible(false)

        setIsChecking(true)

        await requestAttachmentUpload(invoiceId, processId, file, true)
        setShowResult(true)
      } else {
        handleClose()
        await requestAttachmentUpload(invoiceId, processId, file)
      }

      mutate()

      handleClose()

      showToast({
        style: {},
        closable: true,
        severity: 'success',
        summary: t(
          'invoice.attachments.table.upload.dialog.form.upload.messages.success.summary',
        ),
        detail: t(
          'invoice.attachments.table.upload.dialog.form.upload.messages.success.detail',
          {
            name: file.attachment?.name,
          },
        ),
      })
    } catch (error) {
      let handleableError = getHandleableError(error)
      let errorMessage = handleableError.message

      showToast({
        style: {},
        closable: true,
        severity: 'error',
        sticky: true,
        summary: t(
          'invoice.attachments.table.upload.dialog.form.upload.messages.failure.summary',
        ),
        detail: errorMessage,
      })
    } finally {
      setIsRequestRunning(false)
    }
  }

  // deselect and close dialog
  const handleClose = () => {
    formik.resetForm()
    setIsChecking(false)
    setIsUploadFileVisible(true)
    setVisible(false)
  }

  return (
    <UploadAttachmentDialog
      formik={formik}
      isRequestRunning={isRequestRunning}
      isFormFieldValid={isFormFieldValid}
      getFormErrorMessage={getFormErrorMessage}
      handleClose={handleClose}
      visible={visible}
      setVisible={setVisible}
      isChecking={isChecking}
      showResult={showResult}
      isUploadFileVisible={isUploadFileVisible}
      setShowResult={setShowResult}
    />
  )
}
